import React, { useRef, FocusEvent, KeyboardEvent } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";
import { isEscapePressed } from "@sportal/cdk/helpers";

import { SubMenuDropdown, SubMenuItem, TopLinkSubMenu } from "./SubMenu";
import { useIntlTranslate } from "../../../../hooks";
import { Icon } from "../../../icon/Icon";

import "./TopLevelListItem.scss";

export const hasMatchingPath = (items: SubMenuItem[], pathname: string) =>
  items.some((item) => pathname.includes(item.baseUrl || item.url));

export interface TopLevelItem {
  id: string;
  text: string;
  url: string;
  subMenu?: SubMenuItem[];
}

interface TopLevelListItemWithoutSubMenuProps {
  item: TopLevelItem;
}

const itemClassNames = "top-level-list__item";
const linkClassNames = "top-level-list__link";

export const TopLevelListItemWithoutSubMenu = ({
  item,
}: TopLevelListItemWithoutSubMenuProps) => {
  const { url, text } = item;

  return (
    <li className={itemClassNames}>
      <NavLink className={linkClassNames} to={url}>
        <FormattedMessage id={text} />
      </NavLink>
    </li>
  );
};

interface TopLevelListItemWithSubMenuProps {
  item: TopLevelItem;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export const TopLevelListItemWithSubMenu = ({
  item,
  isOpen,
  onOpen,
  onClose,
}: TopLevelListItemWithSubMenuProps) => {
  const topLinkRef = useRef<HTMLLIElement>(null);
  const { pathname } = useLocation();
  const translate = useIntlTranslate();

  const { id, url, text, subMenu } = item;
  const ariaControls = `${id}_submenu`;
  const isActive = hasMatchingPath(subMenu, pathname);

  const handleOnBlur = (e: FocusEvent<HTMLLIElement, Element>) => {
    const menuContainsFocus = topLinkRef.current.contains(e.relatedTarget);

    if (!menuContainsFocus) onClose();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLLIElement>) => {
    if (isEscapePressed(e)) onClose();
  };

  const handleExpandClick = () => {
    isOpen ? onClose() : onOpen();
  };

  const ariaExpanded = isOpen || isActive;

  return (
    <li
      className={classnames(itemClassNames, `${itemClassNames}-with-submenu`, {
        [`${itemClassNames}--top-link-active`]: isActive,
      })}
      ref={topLinkRef}
      onMouseOver={onOpen}
      onMouseLeave={onClose}
      onBlur={handleOnBlur}
      onKeyDown={handleKeyDown}
    >
      <div className="top-level-link-container">
        <NavLink
          className={classnames(linkClassNames, { active: isActive })}
          aria-expanded={ariaExpanded}
          to={url}
        >
          <FormattedMessage id={text} />
        </NavLink>

        <button
          className="sub-menu-dropdown-control"
          type="button"
          disabled={isActive}
          onClick={handleExpandClick}
          aria-expanded={ariaExpanded}
          aria-controls={ariaControls}
          aria-label={translate(`${text}_submenu_control`)}
        >
          <Icon
            icon={`fa-regular fa-angle-${isOpen || isActive ? "down" : "up"}`}
          />
        </button>
      </div>

      {isActive ? (
        <TopLinkSubMenu id={ariaControls} items={subMenu} />
      ) : (
        <SubMenuDropdown id={ariaControls} subMenu={subMenu} isOpen={isOpen} />
      )}
    </li>
  );
};
